import {
  formatHistoryMonth,
  type StudentHistoryCandidate,
  type StudentHistoryImportStatus,
  type StudentNameMatch,
} from './studentWorkoutHistoryImport';

export interface NotesImportRow {
  id: string;
  studentName: string;
  category: string;
  categoryLabel: string;
  periodYear: number | null;
  periodMonth: number | null;
  rawValue: string;
  note: string;
  status: StudentHistoryImportStatus;
  reason: string | null;
  studentId: number | null;
  matchedStudentName: string | null;
  candidateStudents: StudentHistoryCandidate[];
}

export interface NotesMatchResult {
  studentsByName: Record<string, StudentNameMatch>;
  existingNotes: readonly { studentId: number; note: string }[];
}

const NOTES_DOMAIN = 'NotesHistory';
const REASON_STUDENT = 'Aluna não encontrada';
const REASON_STUDENTS = 'Mais de uma aluna com nome compatível';
const REASON_DUPLICATE = 'Já cadastrado';

const CATEGORY_LABELS: Record<string, string> = {
  Anamnesis: 'Anamnese',
  ImportantNotice: 'Aviso importante',
  CancellationAndCredits: 'Cancelamento e créditos',
  TeacherGuidance: 'Orientação para a professora',
  MonthlyObservation: 'Observação do mês',
  HugoObservation: 'Observação Hugo',
  Observation: 'Observação',
};

const statusOrder: Record<StudentHistoryImportStatus, number> = {
  ready: 0,
  ambiguous: 1,
  duplicate: 2,
  blocked: 3,
};

const isRecord = (value: unknown): value is Record<string, unknown> => (
  typeof value === 'object' && value !== null && !Array.isArray(value)
);

const readText = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');

const readInteger = (value: unknown): number | null => {
  const parsed = typeof value === 'number' ? value : Number.NaN;
  return Number.isInteger(parsed) ? parsed : null;
};

const readMonth = (value: unknown): number | null => {
  const parsed = readInteger(value);
  if (parsed === null || parsed < 1 || parsed > 12) {
    return null;
  }
  return parsed;
};

const readYear = (value: unknown): number | null => {
  const parsed = readInteger(value);
  if (parsed === null || parsed < 1900 || parsed > 2100) {
    return null;
  }
  return parsed;
};

export const noteCategoryLabel = (category: string): string => CATEGORY_LABELS[category] ?? category;

export const buildStoredNote = (
  category: string,
  periodYear: number | null,
  periodMonth: number | null,
  rawValue: string,
): string => {
  const label = noteCategoryLabel(category);
  const heading = periodYear !== null && periodMonth !== null
    ? `${label} — ${formatHistoryMonth(periodYear, periodMonth)}`
    : label;
  return `${heading}\n${rawValue.trim()}`;
};

export const formatNotesMonth = (row: Pick<NotesImportRow, 'periodYear' | 'periodMonth'>): string => {
  if (row.periodYear === null || row.periodMonth === null) {
    return 'Sem mês';
  }
  return formatHistoryMonth(row.periodYear, row.periodMonth);
};

const compareRows = (left: NotesImportRow, right: NotesImportRow): number => {
  const leftYear = left.periodYear ?? 9999;
  const rightYear = right.periodYear ?? 9999;
  const leftMonth = left.periodMonth ?? 99;
  const rightMonth = right.periodMonth ?? 99;
  return statusOrder[left.status] - statusOrder[right.status]
    || left.studentName.localeCompare(right.studentName, 'pt-BR')
    || leftYear - rightYear
    || leftMonth - rightMonth
    || left.categoryLabel.localeCompare(right.categoryLabel, 'pt-BR')
    || left.rawValue.localeCompare(right.rawValue, 'pt-BR');
};

const sortRows = (rows: readonly NotesImportRow[]): NotesImportRow[] => rows.slice().sort(compareRows);

const noteKey = (studentId: number, note: string): string => JSON.stringify([studentId, note]);

export const parseStudentNotesImport = (payload: unknown): NotesImportRow[] => {
  if (!isRecord(payload) || !isRecord(payload.metadata) || payload.metadata.domain !== NOTES_DOMAIN) {
    throw new Error('Envie um JSON de histórico de observações (notes-history).');
  }
  if (!Array.isArray(payload.students)) {
    throw new Error('Envie um JSON de histórico de observações (notes-history).');
  }

  const rows: NotesImportRow[] = [];
  payload.students.forEach((student, index) => {
    if (!isRecord(student) || !Array.isArray(student.records)) {
      return;
    }
    const studentName = readText(student.studentName);
    if (studentName.length === 0) {
      return;
    }
    const sourceRowNumber = readInteger(student.sourceRowNumber) ?? index + 1;
    student.records.forEach((record) => {
      if (!isRecord(record)) {
        return;
      }
      const rawValue = readText(record.rawValue);
      if (rawValue.length === 0) {
        return;
      }
      const category = readText(record.category) || 'Observation';
      const periodYear = readYear(record.periodYear);
      const periodMonth = readMonth(record.periodMonth);
      const hasPeriod = periodYear !== null && periodMonth !== null;
      const columnIndex = readInteger(record.sourceColumnIndex) ?? 0;
      rows.push({
        id: `${sourceRowNumber}-${columnIndex}-${rows.length}`,
        studentName,
        category,
        categoryLabel: noteCategoryLabel(category),
        periodYear: hasPeriod ? periodYear : null,
        periodMonth: hasPeriod ? periodMonth : null,
        rawValue,
        note: buildStoredNote(category, hasPeriod ? periodYear : null, hasPeriod ? periodMonth : null, rawValue),
        status: 'ready',
        reason: null,
        studentId: null,
        matchedStudentName: null,
        candidateStudents: [],
      });
    });
  });

  return sortRows(rows);
};

const withStudent = (
  row: NotesImportRow,
  studentId: number,
  fullName: string,
  existing: Set<string>,
): NotesImportRow => {
  const duplicate = existing.has(noteKey(studentId, row.note));
  return {
    ...row,
    status: duplicate ? 'duplicate' : 'ready',
    reason: duplicate ? REASON_DUPLICATE : null,
    studentId,
    matchedStudentName: fullName,
  };
};

export const applyNotesMatches = (
  rows: readonly NotesImportRow[],
  result: NotesMatchResult,
): NotesImportRow[] => {
  const existing = new Set(result.existingNotes.map((item) => noteKey(item.studentId, item.note)));

  return sortRows(rows.map((row) => {
    if (row.status === 'blocked') {
      return row;
    }
    const student = result.studentsByName[row.studentName] ?? { status: 'none' as const };
    if (student.status === 'none') {
      return { ...row, status: 'blocked', reason: REASON_STUDENT };
    }
    if (student.status === 'many') {
      return {
        ...row,
        status: 'ambiguous',
        reason: REASON_STUDENTS,
        candidateStudents: student.candidates,
      };
    }
    return withStudent(row, student.studentId, student.fullName, existing);
  }));
};

export const chooseNotesMatch = (
  rows: readonly NotesImportRow[],
  studentName: string,
  studentId: number | null,
  result: NotesMatchResult,
): NotesImportRow[] => {
  const existing = new Set(result.existingNotes.map((item) => noteKey(item.studentId, item.note)));

  return sortRows(rows.map((row) => {
    if (row.studentName !== studentName || row.status === 'blocked' || row.candidateStudents.length === 0) {
      return row;
    }
    if (studentId === null) {
      return {
        ...row,
        status: 'ambiguous',
        reason: REASON_STUDENTS,
        studentId: null,
        matchedStudentName: null,
      };
    }
    const chosen = row.candidateStudents.find((candidate) => candidate.id === studentId);
    if (!chosen) {
      return row;
    }
    return {
      ...withStudent(row, chosen.id, chosen.fullName, existing),
      candidateStudents: row.candidateStudents,
    };
  }));
};
